import { MagicPolygonHandler } from './MagicPolygonHandler';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { SheetScaleType, SheetShapeDeepFragment } from 'src/gql/graphql';
import { getCalibrationDistance } from 'src/components/viewers/SheetsViewerV2/utils/calibration';
import {
  CROSSHAIR_CURSOR,
  MEASUREMENT_TOOL_SNAPPING_ANGLE_STEP,
  MOVE_CURSOR,
  WAIT_CURSOR,
  VIEWER_TOP_OFFSET,
  InvalidShapeStyle,
  ValidShapeStyle,
  InvalidLineStyle,
  ValidLineStyle,
  VertexStyle,
  FirstVertexStyle,
  HoveredValidLineStyle,
  SnappingIndicatorStyle,
} from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import {
  distanceBetweenInPx,
  getSnapPointByDistance,
  getSnapPointIndicator,
} from 'src/components/viewers/SheetsViewerV2/utils/snapping';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ISheetShape } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { mapPointsToPolylineShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToPolylineShape';
import { mapPointsToSplitLineShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToSplitLineShape';
import { mapPointsToPolygonShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToPolygonShape';
import { PolylineShape } from 'src/components/viewers/SheetsViewerV2/shapes/PolylineShape';
import {
  angleBetweenTwoPoints,
  endPointByAngle,
  snapAngle,
} from 'src/components/viewers/SheetsViewerV2/utils/cartesian';
import {
  findPointLocations,
  PointLocation,
} from 'src/components/viewers/SheetsViewerV2/utils/drawing';
import Edit2D = Autodesk.Extensions.Edit2D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;

export enum DrawingMode {
  Point = 'point',
  Polyline = 'Polyline',
  Rectangle = 'rectangle',
  Polygon = 'polygon',
  MagicPolygon = 'magic-polygon',
  Wedges = 'wedge',
  DrainLine = 'drain-line',
  RoofOutline = 'roof-outline',
  Legend = 'legend',
}

export enum ActiveMode {
  Point = 'point',
  Edge = 'edge',
  Shape = 'shape',
}

type PointWithLocations = {
  point: THREE.Vector3;
  locations: PointLocation[];
};

// Screen-space vertex element that maintains consistent pixel size
class ScreenSpaceVertex {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private pointGetter: () => THREE.Vector3;
  private style: any;
  private isFirstVertex: boolean;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    pointGetter: () => THREE.Vector3,
    style: any,
    isFirstVertex = false
  ) {
    this.viewer = viewer;
    this.pointGetter = pointGetter;
    this.style = style;
    this.isFirstVertex = isFirstVertex;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1000';
    this.element.style.width = '8px';
    this.element.style.height = '8px';
    this.element.style.borderRadius = '50%';
    this.element.style.border = `${this.style.lineWidth}px solid ${this.style.lineColor}`;
    this.element.style.backgroundColor = this.style.fillColor;

    // Add to viewer container
    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    this.updatePosition();
  }

  private updatePosition() {
    // Get the current point position
    const point = this.pointGetter();

    // Convert world coordinates to screen coordinates
    const projected = Autodesk.Viewing.MeasureCommon.project(
      point,
      this.viewer
    );

    // Center the vertex on the point (vertex is 8px wide/high, so subtract 4)
    this.element.style.left = `${projected.x - 4}px`;
    this.element.style.top = `${projected.y - 4}px`;
  }

  public update() {
    this.updatePosition();
  }

  public remove() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Screen-space snapping indicator that shows where a snap point is located
class SnappingIndicator {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private snapPoint: THREE.Vector3 | null = null;

  constructor(viewer: Viewer3D | GuiViewer3D) {
    this.viewer = viewer;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1001';
    this.element.style.boxSizing = 'border-box'; // Include border in size calculation
    this.element.style.width = `${SnappingIndicatorStyle.size}px`;
    this.element.style.height = `${SnappingIndicatorStyle.size}px`;
    this.element.style.border = `${SnappingIndicatorStyle.borderWidth}px solid ${SnappingIndicatorStyle.borderColor}`;
    this.element.style.backgroundColor = SnappingIndicatorStyle.backgroundColor;
    this.element.style.display = 'none';

    // Add to viewer container
    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }
  }

  public setSnapPoint(point: THREE.Vector3 | null): void {
    this.snapPoint = point;
    if (point) {
      this.element.style.display = 'block';
      this.updatePosition();
    } else {
      this.element.style.display = 'none';
    }
  }

  public getSnapPoint(): THREE.Vector3 | null {
    return this.snapPoint;
  }

  private updatePosition(): void {
    if (!this.snapPoint) return;

    // Convert world coordinates to screen coordinates
    const projected = Autodesk.Viewing.MeasureCommon.project(
      this.snapPoint,
      this.viewer
    );

    // Center the indicator on the point
    const halfSize = SnappingIndicatorStyle.size / 2;
    this.element.style.left = `${projected.x - halfSize}px`;
    this.element.style.top = `${projected.y - halfSize}px`;
  }

  public update(): void {
    if (this.snapPoint) {
      this.updatePosition();
    }
  }

  public remove(): void {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

type LengthLabelVariant = 'fixed' | 'intermediate' | 'editable';
type LengthLabelColor = 'gray' | 'green' | 'orange';

// Minimum screen-space distance (in pixels) for a length label to be visible
const MIN_SCREEN_DISTANCE_FOR_LABEL = 50;

// Screen-space length label that shows the distance between two points
class ScreenSpaceLengthLabel {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private startPointGetter: () => THREE.Vector3;
  private endPointGetter: () => THREE.Vector3;
  private viewerToDisplayConverter: (viewerDistance: number) => number;
  private variant: LengthLabelVariant;
  private color: LengthLabelColor;
  private displayUnitSuffix: string;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    startPointGetter: () => THREE.Vector3,
    endPointGetter: () => THREE.Vector3,
    viewerToDisplayConverter: (viewerDistance: number) => number,
    variant: LengthLabelVariant = 'fixed',
    color: LengthLabelColor = 'gray',
    displayUnitSuffix = 'm'
  ) {
    this.viewer = viewer;
    this.startPointGetter = startPointGetter;
    this.endPointGetter = endPointGetter;
    this.viewerToDisplayConverter = viewerToDisplayConverter;
    this.variant = variant;
    this.color = color;
    this.displayUnitSuffix = displayUnitSuffix;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1002';
    this.element.style.transform = 'translate(-50%, -50%)';
    this.element.style.fontSize = '10px';
    this.element.style.fontFamily = 'system-ui, sans-serif';
    this.element.style.padding = '1px 4px';
    this.element.style.borderRadius = '2px';
    this.element.style.whiteSpace = 'nowrap';
    this._applyVariantStyle();

    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    this.updatePosition();
  }

  private _applyVariantStyle(): void {
    switch (this.variant) {
      case 'editable':
        this.element.style.backgroundColor = 'white';
        this.element.style.border = '1px solid #3182ce';
        this.element.style.color = '#3182ce';
        break;
      case 'intermediate':
        this.element.style.backgroundColor = '#718096';
        this.element.style.border = 'none';
        this.element.style.color = 'white';
        break;
      case 'fixed':
      default:
        // Apply color-based styling for fixed labels
        switch (this.color) {
          case 'green':
            this.element.style.backgroundColor = '#48BB78'; // green.400
            this.element.style.border = 'none';
            this.element.style.color = 'white';
            break;
          case 'orange':
            this.element.style.backgroundColor = '#ED8936'; // orange.400
            this.element.style.border = 'none';
            this.element.style.color = 'white';
            break;
          case 'gray':
          default:
            this.element.style.backgroundColor = 'rgba(226, 232, 240, 0.9)';
            this.element.style.border = 'none';
            this.element.style.color = '#4a5568';
            break;
        }
        break;
    }
  }

  private _calculateLength(): number {
    const start = this.startPointGetter();
    const end = this.endPointGetter();
    const viewerDistance = start.distanceTo(end);
    return this.viewerToDisplayConverter(viewerDistance);
  }

  private updatePosition(): void {
    const start = this.startPointGetter();
    const end = this.endPointGetter();

    // Convert start and end to screen coordinates
    const projectedStart = Autodesk.Viewing.MeasureCommon.project(
      start,
      this.viewer
    );
    const projectedEnd = Autodesk.Viewing.MeasureCommon.project(
      end,
      this.viewer
    );

    // Calculate screen-space distance
    const screenDistance = Math.sqrt(
      Math.pow(projectedEnd.x - projectedStart.x, 2) +
        Math.pow(projectedEnd.y - projectedStart.y, 2)
    );

    // Hide label if edge is too short in screen space
    if (screenDistance < MIN_SCREEN_DISTANCE_FOR_LABEL) {
      this.element.style.display = 'none';
      return;
    }
    this.element.style.display = '';

    // Calculate midpoint in screen space
    const midpointX = (projectedStart.x + projectedEnd.x) / 2;
    const midpointY = (projectedStart.y + projectedEnd.y) / 2;

    this.element.style.left = `${midpointX}px`;
    this.element.style.top = `${midpointY}px`;

    // Update the text content
    const length = this._calculateLength();
    this.element.textContent = `${length.toFixed(2)} ${this.displayUnitSuffix}`;
  }

  public update(): void {
    this.updatePosition();
  }

  public setVariant(variant: LengthLabelVariant): void {
    this.variant = variant;
    this._applyVariantStyle();
  }

  public remove(): void {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Screen-space drag feedback line showing the drag vector with an editable length label
class ScreenSpaceDragFeedback {
  private lineElement: HTMLDivElement;
  private labelElement: HTMLDivElement;
  private inputElement: HTMLInputElement | null = null;
  private viewer: Viewer3D | GuiViewer3D;
  private originPoint: THREE.Vector3;
  private currentPoint: THREE.Vector3;
  private viewerToDisplayConverter: (viewerDistance: number) => number;
  private isDragging: boolean;
  private isEditing = false;
  private inputValue = '';
  private onLengthSubmit?: (newLength: number) => void;
  private displayUnitSuffix: string;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    originPoint: THREE.Vector3,
    currentPoint: THREE.Vector3,
    viewerToDisplayConverter: (viewerDistance: number) => number,
    isDragging: boolean,
    onLengthSubmit?: (newLength: number) => void,
    displayUnitSuffix = 'm'
  ) {
    this.viewer = viewer;
    this.originPoint = originPoint.clone();
    this.currentPoint = currentPoint.clone();
    this.viewerToDisplayConverter = viewerToDisplayConverter;
    this.isDragging = isDragging;
    this.onLengthSubmit = onLengthSubmit;
    this.displayUnitSuffix = displayUnitSuffix;

    // Create line element
    this.lineElement = document.createElement('div');
    this.lineElement.style.position = 'absolute';
    this.lineElement.style.pointerEvents = 'none';
    this.lineElement.style.zIndex = '1001'; // Below length labels (1002)
    this.lineElement.style.transformOrigin = '0 0';
    this.lineElement.style.height = '2px';

    // Create label element
    this.labelElement = document.createElement('div');
    this.labelElement.style.position = 'absolute';
    this.labelElement.style.pointerEvents = 'none';
    this.labelElement.style.zIndex = '1003'; // Above length labels (1002)
    this.labelElement.style.transform = 'translate(-50%, -50%)';
    this.labelElement.style.fontSize = '10px';
    this.labelElement.style.fontFamily = 'system-ui, sans-serif';
    this.labelElement.style.padding = '1px 4px';
    this.labelElement.style.borderRadius = '2px';
    this.labelElement.style.whiteSpace = 'nowrap';

    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.lineElement);
      container.appendChild(this.labelElement);
    }

    this._applyStyle();
    this.updatePosition();
  }

  private _applyStyle(): void {
    const color = this.isDragging ? '#718096' : '#3182ce'; // gray.500 : blue.500

    // Dashed line style
    this.lineElement.style.backgroundImage = `repeating-linear-gradient(
      90deg,
      ${color} 0px,
      ${color} 6px,
      transparent 6px,
      transparent 12px
    )`;

    // Label style
    if (this.isDragging) {
      this.labelElement.style.backgroundColor = '#718096';
      this.labelElement.style.border = 'none';
      this.labelElement.style.color = 'white';
    } else {
      this.labelElement.style.backgroundColor = 'white';
      this.labelElement.style.border = '1px solid #3182ce';
      this.labelElement.style.color = '#3182ce';
    }
  }

  private _calculateLength(): number {
    const viewerDistance = this.originPoint.distanceTo(this.currentPoint);
    return this.viewerToDisplayConverter(viewerDistance);
  }

  private updatePosition(): void {
    const originProjected = Autodesk.Viewing.MeasureCommon.project(
      this.originPoint,
      this.viewer
    );
    const currentProjected = Autodesk.Viewing.MeasureCommon.project(
      this.currentPoint,
      this.viewer
    );

    // Calculate line length and angle
    const dx = currentProjected.x - originProjected.x;
    const dy = currentProjected.y - originProjected.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Position and rotate line
    this.lineElement.style.left = `${originProjected.x}px`;
    this.lineElement.style.top = `${originProjected.y}px`;
    this.lineElement.style.width = `${length}px`;
    this.lineElement.style.transform = `rotate(${angle}rad)`;

    // Position label at midpoint
    const midX = (originProjected.x + currentProjected.x) / 2;
    const midY = (originProjected.y + currentProjected.y) / 2;
    this.labelElement.style.left = `${midX}px`;
    this.labelElement.style.top = `${midY}px`;

    // Update label text
    if (this.isEditing && this.inputElement) {
      // Don't update text while editing
    } else {
      const displayLength = this._calculateLength();
      this.labelElement.textContent = `${displayLength.toFixed(2)} ${
        this.displayUnitSuffix
      }`;
    }
  }

  public setCurrentPoint(point: THREE.Vector3): void {
    this.currentPoint = point.clone();
    this.updatePosition();
  }

  public setDragging(isDragging: boolean): void {
    this.isDragging = isDragging;
    this._applyStyle();
  }

  public update(): void {
    this.updatePosition();
  }

  public startEditing(): void {
    if (this.isEditing || this.isDragging) return;
    this.isEditing = true;
    this.inputValue = '';

    // Replace label with input
    this.labelElement.textContent = '';
    this.labelElement.style.pointerEvents = 'auto';
    this.labelElement.style.padding = '0';

    this.inputElement = document.createElement('input');
    this.inputElement.type = 'text';
    this.inputElement.style.width = '45px';
    this.inputElement.style.height = '18px';
    this.inputElement.style.fontSize = '10px';
    this.inputElement.style.fontFamily = 'system-ui, sans-serif';
    this.inputElement.style.border = '1px solid #3182ce';
    this.inputElement.style.borderRadius = '2px';
    this.inputElement.style.padding = '0 4px';
    this.inputElement.style.outline = 'none';
    this.inputElement.style.textAlign = 'right';

    this.inputElement.addEventListener('keydown', this._onInputKeyDown);
    this.inputElement.addEventListener('blur', this._onInputBlur);

    this.labelElement.appendChild(this.inputElement);
    this.inputElement.focus();
  }

  public appendToInput(char: string): void {
    if (!this.isEditing) {
      this.startEditing();
    }
    if (this.inputElement) {
      this.inputValue += char;
      this.inputElement.value = this.inputValue;
    }
  }

  private _onInputKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this._submitInput();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._cancelEditing();
    }
  };

  private _onInputBlur = (): void => {
    this._cancelEditing();
  };

  private _submitInput(): void {
    if (!this.inputElement) return;
    const value = parseFloat(this.inputElement.value);
    if (!isNaN(value) && value > 0 && this.onLengthSubmit) {
      this.onLengthSubmit(value);
    }
    this._cancelEditing();
  }

  private _cancelEditing(): void {
    if (!this.isEditing) return;
    this.isEditing = false;
    this.inputValue = '';

    if (this.inputElement) {
      this.inputElement.removeEventListener('keydown', this._onInputKeyDown);
      this.inputElement.removeEventListener('blur', this._onInputBlur);
      this.inputElement.remove();
      this.inputElement = null;
    }

    this.labelElement.style.pointerEvents = 'none';
    this.labelElement.style.padding = '1px 4px';
    this.updatePosition();
  }

  public isEditingActive(): boolean {
    return this.isEditing;
  }

  public remove(): void {
    if (this.inputElement) {
      this.inputElement.removeEventListener('keydown', this._onInputKeyDown);
      this.inputElement.removeEventListener('blur', this._onInputBlur);
    }
    if (this.lineElement.parentNode) {
      this.lineElement.parentNode.removeChild(this.lineElement);
    }
    if (this.labelElement.parentNode) {
      this.labelElement.parentNode.removeChild(this.labelElement);
    }
  }
}

export class ShapesDrawingTool extends Autodesk.Viewing.ToolInterface {
  names = [ActiveTool.Drawing];
  shapes: ISheetShape[] = [];
  private screenSpaceVertices: ScreenSpaceVertex[] = [];
  private screenSpaceLengthLabels: ScreenSpaceLengthLabel[] = [];
  private intermediateLengthLabel: ScreenSpaceDragFeedback | null = null;
  private dragFeedback: ScreenSpaceDragFeedback | null = null;
  private snappingIndicator: SnappingIndicator | null = null;
  private pointsArray: THREE.Vector3[][] = [];
  private pointsArrayIndex = 0;
  private cursorPoint: THREE.Vector3 | null = null;
  private hoveredPoint: PointWithLocations | null = null;
  private hoveredLine: THREE.Line3 | null = null;
  private hoveredLineShape: PolylineShape | null = null;
  private activePoints: PointWithLocations[] = [];
  private activePointsMode: ActiveMode | null = null;
  private activePointerInitPosition: THREE.Vector3 | null = null;
  private activePointerCurrentPosition: THREE.Vector3 | null = null;
  // Track the actual geometry origin for drag feedback (vertex position or edge midpoint)
  private dragOriginPoint: THREE.Vector3 | null = null;
  private dragCurrentPoint: THREE.Vector3 | null = null;
  private dragDirection: THREE.Vector3 | null = null;
  // Store the points being dragged for length submission
  private draggedPoints: PointWithLocations[] = [];
  private lastDragMode: ActiveMode | null = null;
  private magicPolygonHandler: MagicPolygonHandler = new MagicPolygonHandler();
  // Calibration scale: viewer units to real-world meters
  private _calibrationScale: number | null = null;
  private _keyDownHandler: ((event: KeyboardEvent) => void) | null = null;
  private buttonDownShiftKey = false;
  private isMagicPolygonLoading = false;

  // Simple undo/redo history for the current drawing session
  private historyPast: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }[] = [];
  private historyFuture: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }[] = [];

  // Vertex selection state (geometric points, independent of shapes array indices)
  private selectedPoints: THREE.Vector3[] = [];

  // Box selection state for vertices (screen-space, relative to viewer container)
  private vertexBoxActive = false;
  private vertexBoxStart: { x: number; y: number } | null = null;
  private vertexBoxCurrent: { x: number; y: number } | null = null;
  private vertexBoxElement: HTMLDivElement | null = null;

  // Pointer handlers for vertex box selection (Ctrl/Cmd + drag)
  private _onVertexBoxPointerDown = (event: PointerEvent) => {
    // Only respond to left button with Ctrl/Cmd
    if (event.button !== 0 || (!event.ctrlKey && !event.metaKey)) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._startVertexBoxSelection(clientX, clientY);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  private _onVertexBoxPointerMove = (event: PointerEvent) => {
    if (!this.vertexBoxActive) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._updateVertexBoxSelection(clientX, clientY);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  private _onVertexBoxPointerUp = (event: PointerEvent) => {
    if (!this.vertexBoxActive) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._endVertexBoxSelection(clientX, clientY, event.shiftKey);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly snapper: Autodesk.Viewing.Extensions.Snapping.Snapper,
    private readonly edit2d: Edit2D,
    private readonly activeSheetData: ActiveSheetData,
    public readonly drawingMode: DrawingMode,
    public readonly onUpdate: (points: THREE.Vector3[][]) => void,
    private readonly initialPoints: THREE.Vector3[][] | null = null,
    private readonly calibration: SheetScaleType | null = null,
    private readonly renderedSheetShapes: Record<
      string,
      SheetShapeDeepFragment[]
    > | null = null,
    private readonly displayUnitSuffix: string = 'm'
  ) {
    super();

    // Calculate calibration scale once for consistent length calculations
    this._calibrationScale = this._computeCalibrationScale();

    if (this.initialPoints) {
      this.pointsArray = this.initialPoints;
      this.pointsArrayIndex =
        this.drawingMode === DrawingMode.Polyline
          ? this.initialPoints.length - 1 // Continue drawing from the last point of the initial points
          : this.initialPoints.length;
      this.onUpdate([...this.pointsArray]);
    }

    delete (this as any).activate;
    delete (this as any).deactivate;
    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).handleDoubleClick;
    delete (this as any).handleButtonDown;
    delete (this as any).handleButtonUp;
  }

  /**
   * Compute the calibration scale for converting viewer units to real-world units.
   * Returns the scale factor (real-world units per viewer unit), or null if no calibration.
   * The result is in calibration units (MM, FEET, or M).
   */
  private _computeCalibrationScale(): number | null {
    if (!this.calibration) {
      return null;
    }

    const calibrationDistance = getCalibrationDistance(
      this.viewer,
      this.activeSheetData,
      this.calibration
    );

    if (calibrationDistance <= 0) {
      return null;
    }

    // Scale = destination value / viewer distance
    // This gives us calibration units per viewer unit
    return this.calibration.destinationWidth / calibrationDistance;
  }

  /**
   * Get the display unit suffix. Falls back to "px" if not calibrated.
   */
  private _getDisplayUnitSuffix(): string {
    if (this._calibrationScale === null) {
      return 'px';
    }
    return this.displayUnitSuffix;
  }

  /**
   * Get the conversion factor from calibration units to meters.
   */
  private _getCalibrationToMeters(): number {
    if (!this.calibration) return 1;
    switch (this.calibration.destinationUnit) {
      case 'MM':
        return 1 / 1000;
      case 'FEET':
        return 0.3048;
      case 'M':
      default:
        return 1;
    }
  }

  /**
   * Get the conversion factor from display units to meters.
   */
  private _getDisplayToMeters(): number {
    switch (this.displayUnitSuffix) {
      case 'ft':
        return 0.3048;
      case 'in':
        return 0.0254;
      case 'mm':
        return 1 / 1000;
      case 'cm':
        return 1 / 100;
      case 'dm':
        return 1 / 10;
      case 'm':
      default:
        return 1;
    }
  }

  /**
   * Convert a viewer distance to display units (for showing to user).
   * Handles the full conversion chain: viewer → calibration units → meters → display units.
   */
  private _viewerToDisplayUnits(viewerDistance: number): number {
    if (this._calibrationScale === null || !this.calibration) {
      return viewerDistance; // No calibration, return raw distance
    }

    const inCalibrationUnits = viewerDistance * this._calibrationScale;
    const calibToMeters = this._getCalibrationToMeters();
    const displayToMeters = this._getDisplayToMeters();
    const inMeters = inCalibrationUnits * calibToMeters;
    return inMeters / displayToMeters;
  }

  /**
   * Convert display units (from user input) to viewer distance.
   * Handles the full conversion chain: display units → meters → calibration units → viewer.
   */
  private _displayUnitsToViewer(displayValue: number): number {
    if (this._calibrationScale === null || !this.calibration) {
      return displayValue; // No calibration, return as-is
    }

    const displayToMeters = this._getDisplayToMeters();
    const calibToMeters = this._getCalibrationToMeters();
    const inMeters = displayValue * displayToMeters;
    const inCalibrationUnits = inMeters / calibToMeters;
    return inCalibrationUnits / this._calibrationScale;
  }

  /**
   * Finalize drag feedback when button is released.
   * If moved, set to editable state and store state for submission.
   * If not moved, clear the feedback.
   */
  private _finalizeDragFeedback(moved: boolean): void {
    if (moved && this.dragFeedback) {
      // Change to non-dragging state so user can edit the length
      this.dragFeedback.setDragging(false);
      // Store state for length submission
      if (this.dragOriginPoint) {
        this._storeDragStateForSubmission();
      }
    } else if (!moved && this.dragFeedback) {
      // If we didn't move, clear the drag feedback
      this.dragFeedback.remove();
      this.dragFeedback = null;
    }
  }

  /**
   * Store the current drag state for subsequent length submissions.
   * Called when drag ends with movement.
   * NOTE: At this point, pointsArray has ALREADY been updated with the moved positions
   * in handleButtonUp, so we use pointsArray directly (not _mapPointsArrayAndActivePoints).
   */
  private _storeDragStateForSubmission(): void {
    this.draggedPoints = this.activePoints.map((p) => ({
      point: p.point.clone(),
      locations: p.locations,
    }));
    this.lastDragMode = this.activePointsMode;

    // Store the current position (after drag) for subsequent length submissions
    // Use pointsArray directly since it already has the moved positions
    if (
      this.activePointsMode === ActiveMode.Edge &&
      this.activePoints.length === 2
    ) {
      const [si1, pi1] = this.activePoints[0].locations[0];
      const [si2, pi2] = this.activePoints[1].locations[0];
      const p1 = this.pointsArray[si1]?.[pi1];
      const p2 = this.pointsArray[si2]?.[pi2];
      if (p1 && p2) {
        this.dragCurrentPoint = new THREE.Vector3()
          .addVectors(p1, p2)
          .multiplyScalar(0.5);
      }
    } else if (this.activePoints.length > 0) {
      const [si, pi] = this.activePoints[0].locations[0];
      const p = this.pointsArray[si]?.[pi];
      if (p) {
        this.dragCurrentPoint = p.clone();
      }
    }
  }

  getCursor(): string {
    // Show loading cursor when waiting for magic polygon API response
    if (
      this.drawingMode === DrawingMode.MagicPolygon &&
      this.isMagicPolygonLoading
    ) {
      return WAIT_CURSOR;
    }
    if (this.activePoints.length > 0) return MOVE_CURSOR;
    if (this.hoveredPoint) return MOVE_CURSOR;
    if (this.hoveredLine || this.hoveredLineShape) return MOVE_CURSOR;
    return CROSSHAIR_CURSOR;
  }

  activate() {
    // Create snapping indicator
    this.snappingIndicator = new SnappingIndicator(this.viewer);

    // Immediately draw any existing shapes when the tool is activated
    this.draw();

    // Set up camera change listener to update screen-space vertices
    this.viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Attach pointer listeners for vertex box selection (capture phase to beat viewer tools)
    const container = this.viewer.container as HTMLElement | null;
    if (container) {
      container.addEventListener(
        'pointerdown',
        this._onVertexBoxPointerDown,
        true
      );
      container.addEventListener(
        'pointermove',
        this._onVertexBoxPointerMove,
        true
      );
      container.addEventListener('pointerup', this._onVertexBoxPointerUp, true);
    }

    // Keyboard handlers for undo / redo (Ctrl/Cmd+Z, Ctrl/Cmd+Y)
    // eslint-disable-next-line complexity
    this._keyDownHandler = (event: KeyboardEvent) => {
      const isUndo =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z';
      const isRedo =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y';
      const isDelete = event.key === 'Delete' || event.key === 'Backspace';

      // Handle numeric input for drag feedback editing
      const isNumericKey = /^[0-9.]$/.test(event.key);
      if (isNumericKey) {
        // Drag feedback after dragging
        if (this.dragFeedback && !this.dragFeedback.isEditingActive()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.dragFeedback.appendToInput(event.key);
          return true;
        }
      }

      if (!isUndo && !isRedo && !isDelete) {
        return false;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (isUndo) {
        this._undo();
        // Clear drag feedback on undo
        if (this.dragFeedback) {
          this.dragFeedback.remove();
          this.dragFeedback = null;
        }
      } else if (isRedo) {
        this._redo();
        // Clear drag feedback on redo
        if (this.dragFeedback) {
          this.dragFeedback.remove();
          this.dragFeedback = null;
        }
      } else if (isDelete) {
        // Delete selected vertices (geometry change)
        if (this.selectedPoints.length > 0) {
          this._pushHistorySnapshot();
          const toRemove = [...this.selectedPoints];
          this._clearVertexSelection();
          toRemove.forEach((point) => this._removePoint(point));
          this.draw();
          this.onUpdate?.([...this.pointsArray]);
        }
      }

      return true;
    };

    // Use capture phase to ensure we handle the event before the viewer
    window.addEventListener('keydown', this._keyDownHandler, true);
  }

  deactivate() {
    this.pointsArray = [];
    this.pointsArrayIndex = 0;
    this.cursorPoint = null;
    this.hoveredPoint = null;
    this.hoveredLine = null;
    this.activePoints = [];
    this.activePointsMode = null;
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;
    this.buttonDownShiftKey = false;
    this.clear();

    // Clear drag feedback on deactivate
    if (this.dragFeedback) {
      this.dragFeedback.remove();
      this.dragFeedback = null;
    }
    // Clear intermediate length label
    if (this.intermediateLengthLabel) {
      this.intermediateLengthLabel.remove();
      this.intermediateLengthLabel = null;
    }
    // Clear drag state
    this.dragOriginPoint = null;
    this.dragCurrentPoint = null;
    this.dragDirection = null;
    this.draggedPoints = [];
    this.lastDragMode = null;

    // Reset MagicPolygon handler to ensure proper cleanup
    this.magicPolygonHandler.reset();

    // Remove camera change listener
    this.viewer.removeEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Remove vertex box pointer listeners
    const container = this.viewer.container as HTMLElement | null;
    if (container) {
      container.removeEventListener(
        'pointerdown',
        this._onVertexBoxPointerDown,
        true
      );
      container.removeEventListener(
        'pointermove',
        this._onVertexBoxPointerMove,
        true
      );
      container.removeEventListener(
        'pointerup',
        this._onVertexBoxPointerUp,
        true
      );
    }

    // Remove keyboard event listener
    if (this._keyDownHandler) {
      window.removeEventListener('keydown', this._keyDownHandler, true);
      this._keyDownHandler = null;
    }

    // Clear vertex selection and box state
    this.selectedPoints = [];
    if (this.vertexBoxElement && this.vertexBoxElement.parentNode) {
      this.vertexBoxElement.parentNode.removeChild(this.vertexBoxElement);
    }
    this.vertexBoxElement = null;
    this.vertexBoxActive = false;
    this.vertexBoxStart = null;
    this.vertexBoxCurrent = null;

    // Clean up snapping indicator
    if (this.snappingIndicator) {
      this.snappingIndicator.remove();
      this.snappingIndicator = null;
    }
  }

  private _onCameraChange = () => {
    // Update all screen-space vertices when camera changes
    this.screenSpaceVertices.forEach((vertex) => vertex.update());
    // Update all screen-space length labels when camera changes
    this.screenSpaceLengthLabels.forEach((label) => label.update());
    // Update intermediate length label position
    this.intermediateLengthLabel?.update();
    // Update drag feedback position
    this.dragFeedback?.update();
    // Update snapping indicator position
    this.snappingIndicator?.update();
  };

  private _handleMagicPolygonButtonDown(
    event: MouseEvent,
    button: number
  ): boolean {
    if (button === 0) {
      // Clear any existing drag feedback when starting a new interaction
      if (this.dragFeedback) {
        this.dragFeedback.remove();
        this.dragFeedback = null;
      }

      // Refresh hover state to ensure we have the latest hovered point/edge
      this._updateHoveredPointFromEvent(event);
      this._updateHoveredLineFromEvent(event);

      // Store shift key state for use in handleButtonUp
      this.buttonDownShiftKey = event.shiftKey;

      // Left click - handle normally
      this.activePointerInitPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );
      this.activePointerCurrentPosition = this.activePointerInitPosition;

      // Clear stored drag state
      this.dragOriginPoint = null;
      this.dragCurrentPoint = null;
      this.dragDirection = null;
      this.draggedPoints = [];
      this.lastDragMode = null;

      // Check if we're clicking on a vertex or edge for editing
      // For MagicPolygon, we can safely set activePoints immediately.
      // Whether it's treated as a click or a drag is decided in handleButtonUp via the 'moved' flag.
      if (
        this.hoveredPoint &&
        this.pointsArray[this.pointsArrayIndex]?.length > 0
      ) {
        this.activePointsMode = ActiveMode.Point;
        this.activePoints = [this.hoveredPoint];
        // Capture the original vertex position for drag feedback
        this.dragOriginPoint = this.hoveredPoint.point.clone();
        return true; // Consume event to prevent viewer from handling it
      } else if (
        this.hoveredLine &&
        this.pointsArray[this.pointsArrayIndex]?.length > 0
      ) {
        this.activePointsMode = ActiveMode.Edge;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const start: THREE.Vector3 = this.hoveredLine.start;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const end: THREE.Vector3 = this.hoveredLine.end;
        this.activePoints = [
          {
            point: start,
            locations: findPointLocations(this.pointsArray, start),
          },
          {
            point: end,
            locations: findPointLocations(this.pointsArray, end),
          },
        ];
        // Capture the original edge midpoint for drag feedback
        this.dragOriginPoint = new THREE.Vector3()
          .addVectors(start, end)
          .multiplyScalar(0.5);
        return true; // Consume event to prevent viewer from handling it
      }

      // Not clicking on vertex/edge - allow normal click handling
      return true; // Consume event for MagicPolygon mode
    }
    // Right click - don't consume, allow panning
    return false;
  }

  handleButtonDown(event: MouseEvent, button: number): boolean {
    // For MagicPolygon, only handle left-click events, let right-click events pass through for panning
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      return this._handleMagicPolygonButtonDown(event, button);
    }

    return this._handleRegularButtonDown(event, button);
  }

  private _clonePointsArray(points: THREE.Vector3[][]): THREE.Vector3[][] {
    return points.map((shape) => shape.map((p) => p.clone()));
  }

  private _pushHistorySnapshot(): void {
    this.historyPast.push({
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    });
    this.historyFuture = [];
  }

  private _restoreSnapshot(snapshot: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }): void {
    this.pointsArray = this._clonePointsArray(snapshot.pointsArray);
    this.pointsArrayIndex = snapshot.pointsArrayIndex;
    this.draw();
    this.onUpdate?.([...this.pointsArray]);
  }

  private _undo(): void {
    if (this.historyPast.length === 0) {
      return;
    }
    const previous = this.historyPast.pop() as {
      pointsArray: THREE.Vector3[][];
      pointsArrayIndex: number;
    };
    const current = {
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    };
    this.historyFuture.unshift(current);
    this._restoreSnapshot(previous);
  }

  private _redo(): void {
    if (this.historyFuture.length === 0) {
      return;
    }
    const next = this.historyFuture.shift() as {
      pointsArray: THREE.Vector3[][];
      pointsArrayIndex: number;
    };
    const current = {
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    };
    this.historyPast.push(current);
    this._restoreSnapshot(next);
  }

  private _startVertexBoxSelection(x: number, y: number): void {
    this.vertexBoxActive = true;
    this.vertexBoxStart = { x, y };
    this.vertexBoxCurrent = { x, y };

    const container = this.viewer.container as HTMLElement | null;
    if (!container) {
      return;
    }

    const box = document.createElement('div');
    box.style.position = 'absolute';
    box.style.zIndex = '2000';
    box.style.pointerEvents = 'none';
    box.style.border = '1px solid rgba(0, 136, 255, 0.8)';
    box.style.backgroundColor = 'rgba(0, 136, 255, 0.15)';

    container.appendChild(box);
    this.vertexBoxElement = box;
  }

  private _updateVertexBoxSelection(x: number, y: number): void {
    if (
      !this.vertexBoxActive ||
      !this.vertexBoxStart ||
      !this.vertexBoxElement
    ) {
      return;
    }

    this.vertexBoxCurrent = { x, y };

    const start = this.vertexBoxStart;
    const end = this.vertexBoxCurrent;

    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(start.x - end.x);
    const height = Math.abs(start.y - end.y);

    this.vertexBoxElement.style.left = `${left}px`;
    this.vertexBoxElement.style.top = `${top}px`;
    this.vertexBoxElement.style.width = `${width}px`;
    this.vertexBoxElement.style.height = `${height}px`;
  }

  private _endVertexBoxSelection(
    x: number,
    y: number,
    additive: boolean
  ): void {
    if (!this.vertexBoxActive || !this.vertexBoxStart) {
      return;
    }

    this.vertexBoxCurrent = { x, y };

    const start = this.vertexBoxStart;
    const end = this.vertexBoxCurrent;

    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    // Ignore very small boxes (treat as click)
    if (Math.abs(maxX - minX) < 5 || Math.abs(maxY - minY) < 5) {
      this._clearVertexBoxVisuals();
      return;
    }

    const newlySelected: THREE.Vector3[] = [];

    // For each point in all shapes, project to screen space and test inside box
    this.pointsArray.forEach((shape) => {
      shape.forEach((point) => {
        const projected = Autodesk.Viewing.MeasureCommon.project(
          point,
          this.viewer
        );
        if (
          projected.x >= minX &&
          projected.x <= maxX &&
          projected.y >= minY &&
          projected.y <= maxY
        ) {
          newlySelected.push(point.clone());
        }
      });
    });

    if (!additive) {
      this.selectedPoints = newlySelected;
    } else {
      // Union with existing selection
      newlySelected.forEach((p) => {
        if (!this._isPointSelected(p)) {
          this.selectedPoints.push(p);
        }
      });
    }

    this._clearVertexBoxVisuals();
    this.draw();
  }

  private _clearVertexBoxVisuals(): void {
    this.vertexBoxActive = false;
    this.vertexBoxStart = null;
    this.vertexBoxCurrent = null;
    if (this.vertexBoxElement && this.vertexBoxElement.parentNode) {
      this.vertexBoxElement.parentNode.removeChild(this.vertexBoxElement);
    }
    this.vertexBoxElement = null;
  }

  private _isPointSelected(point: THREE.Vector3): boolean {
    return this.selectedPoints.some((sel) => sel.equals(point));
  }

  private _clearVertexSelection(): void {
    this.selectedPoints = [];
  }

  private _toggleVertexSelection(
    point: THREE.Vector3,
    additive: boolean
  ): void {
    if (!additive) {
      // Replace selection
      this.selectedPoints = [point.clone()];
      return;
    }

    const alreadySelected = this._isPointSelected(point);
    if (alreadySelected) {
      this.selectedPoints = this.selectedPoints.filter(
        (sel) => !sel.equals(point)
      );
    } else {
      this.selectedPoints.push(point.clone());
    }
  }

  private _handleRegularButtonDown(event: MouseEvent, button: number): boolean {
    // Clear any existing drag feedback when starting a new interaction
    if (this.dragFeedback) {
      this.dragFeedback.remove();
      this.dragFeedback = null;
    }
    // Clear stored drag state
    this.dragOriginPoint = null;
    this.dragCurrentPoint = null;
    this.dragDirection = null;
    this.draggedPoints = [];
    this.lastDragMode = null;

    this.activePointerInitPosition = this._getWorldPositionFromMouseEvent(
      event,
      false
    );
    this.activePointerCurrentPosition = this.activePointerInitPosition;

    if (this.hoveredPoint && button === 0) {
      this.activePointsMode = ActiveMode.Point;
      this.activePoints = [this.hoveredPoint];
      // Capture the original vertex position for drag feedback
      this.dragOriginPoint = this.hoveredPoint.point.clone();
      return true;
    } else if (this.hoveredLine && button === 0) {
      this.activePointsMode = ActiveMode.Edge;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const start: THREE.Vector3 = this.hoveredLine.start;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const end: THREE.Vector3 = this.hoveredLine.end;
      this.activePoints = [
        {
          point: start,
          locations: findPointLocations(this.pointsArray, start),
        },
        {
          point: end,
          locations: findPointLocations(this.pointsArray, end),
        },
      ];
      // Capture the original edge midpoint for drag feedback
      this.dragOriginPoint = new THREE.Vector3()
        .addVectors(start, end)
        .multiplyScalar(0.5);
      return true;
    }

    return false;
  }

  private _handleMagicPolygonButtonUp(
    event: MouseEvent,
    moved: boolean
  ): boolean {
    if (moved) {
      // Snapshot before applying drag so the whole drag is a single undo step
      this._pushHistorySnapshot();
      this.pointsArray = this._mapPointsArrayAndActivePoints();
      this.magicPolygonHandler.markUserInteraction();
      this.onUpdate([...this.pointsArray]);
    } else if (
      this.activePointsMode === ActiveMode.Point &&
      this.pointsArray[this.pointsArrayIndex]?.length > 0
    ) {
      // No movement - update hover states and check if clicking on a vertex for selection
      this._updateHoveredPointFromEvent(event);
      if (this.hoveredPoint) {
        // No movement and clicking on a vertex - handle selection
        // Use the shift key state from buttonDown to ensure it's preserved
        const additive = this.buttonDownShiftKey;
        this._toggleVertexSelection(this.hoveredPoint.point, additive);
      }
    }
    // Clear shift key state
    this.buttonDownShiftKey = false;
    return true; // Always consume event in MagicPolygon mode
  }

  private _handleRegularButtonUp(
    event: MouseEvent,
    moved: boolean,
    hadActivePoints: boolean
  ): boolean {
    // CRITICAL: Capture the final pointsArray state BEFORE clearing active points
    // If we moved, pointsArray should already be updated with moved points
    // Create a deep clone for onUpdate to ensure the hook gets independent Vector3 objects
    const finalPointsArray = this.pointsArray.map((points) =>
      points.map((p) => p.clone())
    );

    // Finalize drag feedback and store state for length submission
    this._finalizeDragFeedback(moved);

    // Clear active points state BEFORE draw/onUpdate
    // This ensures draw() uses the updated pointsArray directly, not _mapPointsArrayAndActivePoints()
    this.activePoints = [];
    this.activePointsMode = null;
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;

    // CRITICAL: Update pointsArray with the final state to ensure draw() uses it
    // This is important because draw() calls _mapPointsArrayAndActivePoints() which returns pointsArray
    // when activePoints is cleared, so we need to ensure pointsArray has the updated values
    if (moved) {
      this.pointsArray = finalPointsArray.map((points) =>
        points.map((p) => p.clone())
      );
    }

    // Draw with the updated pointsArray
    // Since activePoints is cleared, _mapPointsArrayAndActivePoints() will return this.pointsArray directly
    this.draw();

    // CRITICAL: Always call onUpdate if we had active points (dragging) to ensure the hook gets the final state
    // This must happen AFTER updating pointsArray and clearing activePoints
    if (hadActivePoints) {
      // Use the cloned array to ensure the hook gets independent Vector3 objects
      this.onUpdate(finalPointsArray);
    }

    // Return true if we clicked on a vertex/edge (even without dragging) to prevent camera nudging
    // or if we actually dragged
    return moved || hadActivePoints;
  }

  private _computeMovedFlag(event: MouseEvent): {
    moved: boolean;
    hadActivePoints: boolean;
  } {
    const cursorPosition = this._getWorldPositionFromMouseEvent(event, false);
    const hadActivePoints = this.activePoints.length > 0;

    let moved = false;
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // For MagicPolygon, use a small *pixel* movement threshold to distinguish click vs drag
      if (hadActivePoints && cursorPosition && this.activePointerInitPosition) {
        const pxDistance =
          distanceBetweenInPx(
            this.viewer,
            cursorPosition,
            this.activePointerInitPosition
          ) ?? 0;
        moved = pxDistance > 3; // ~3px threshold for reliable small drags
      } else {
        moved = false;
      }
    } else {
      // For regular modes (Polygon, Rectangle, etc.), treat any mouse up with
      // active points as a movement commit to avoid snap-back.
      moved = hadActivePoints;
    }

    return { moved, hadActivePoints };
  }

  handleButtonUp(event: MouseEvent): boolean {
    // Finish vertex box selection, if active
    if (this.vertexBoxActive && this.vertexBoxStart) {
      const x = event.clientX;
      const y = event.clientY - VIEWER_TOP_OFFSET;
      this._endVertexBoxSelection(x, y, event.shiftKey);
      return true;
    }

    // For MagicPolygon, let right-click button up pass through so the viewer
    // can correctly stop panning. We only care about left-button for edits.
    if (this.drawingMode === DrawingMode.MagicPolygon && event.button === 2) {
      return false;
    }

    const { moved, hadActivePoints } = this._computeMovedFlag(event);

    // Handle MagicPolygon mode separately
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // Let _handleMagicPolygonButtonUp handle snapshot, geometry commit and onUpdate
      const result = this._handleMagicPolygonButtonUp(event, moved);
      // Finalize drag feedback BEFORE clearing activePoints (needed for state storage)
      this._finalizeDragFeedback(moved);
      // Clear dragging state
      this.activePoints = [];
      this.activePointsMode = null;
      this.activePointerInitPosition = null;
      this.activePointerCurrentPosition = null;
      this.draw();
      return result;
    }

    // For regular modes, update pointsArray if moved
    if (moved) {
      // Snapshot before applying drag so the whole drag is a single undo step
      this._pushHistorySnapshot();
      // Update pointsArray with the mapped (moved) points
      // CRITICAL: Get the final mapped points BEFORE clearing active points
      const mappedPoints = this._mapPointsArrayAndActivePoints();
      this.pointsArray = mappedPoints.map((points) =>
        points.map((p) => p.clone())
      );
    }

    return this._handleRegularButtonUp(event, moved, hadActivePoints);
  }

  // eslint-disable-next-line complexity
  handleSingleClick(event: MouseEvent, button: number): boolean {
    // Update hover states first to ensure we have accurate hover information
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Vertex selection: click on an existing vertex selects/toggles it (works for all modes including MagicPolygon)
    if (button === 0 && this.hoveredPoint) {
      // For MagicPolygon mode, selection is handled in handleButtonUp to ensure shift key state is preserved
      // Skip selection handling here to avoid conflicts
      if (this.drawingMode === DrawingMode.MagicPolygon) {
        // Selection already handled in handleButtonUp, just return
        return true;
      }

      // For Polygon mode, check if clicking on the first vertex to close the polygon
      if (this.drawingMode === DrawingMode.Polygon) {
        const currentShape = this.pointsArray[this.pointsArrayIndex];
        const firstPoint = currentShape?.[0];
        // If clicking on the first vertex and we have at least 3 points, close the polygon
        if (
          firstPoint &&
          this.hoveredPoint.point.equals(firstPoint) &&
          currentShape.length >= 3
        ) {
          this._pushHistorySnapshot();
          this._closePolygons();
          this.pointsArrayIndex++;
          this.draw();
          this.onUpdate([...this.pointsArray]);
          return true;
        }
      }

      // For other modes, handle vertex selection normally
      const additive = event.shiftKey;
      this._toggleVertexSelection(this.hoveredPoint.point, additive);
      this.draw();
      return true;
    }

    // For MagicPolygon, add a point and let the hook handle the API call
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      if (button === 0) {
        // Only add a new point if we're not clicking on a vertex or edge
        if (
          this.magicPolygonHandler.shouldAddSamplePoint(
            this.hoveredPoint,
            this.hoveredLine
          )
        ) {
          const cursorPosition = this._getWorldPositionFromMouseEvent(event);
          this._pushHistorySnapshot();

          // Instead of adding to pointsArray (the result polygon), add to sample points
          this.magicPolygonHandler.addSamplePoint(cursorPosition);

          this.draw();
          // Trigger update so the hook sees the new sample point
          this.onUpdate([...this.pointsArray]);
        }
        // If we're clicking on a vertex or edge, the handleButtonDown method will handle the dragging
        return true;
      }
      return true;
    }

    const currentShape = this.pointsArray[this.pointsArrayIndex];
    const lastPoint = currentShape?.[currentShape.length - 1];
    const cursorPositionRaw = this._getWorldPositionFromMouseEvent(event);
    // Apply snapping when clicking to place a point
    const snappedPosition = this._getSnappedPoint(cursorPositionRaw);
    const cursorPosition =
      lastPoint && event.shiftKey
        ? this._calculateEndPoint(lastPoint, snappedPosition, true)
        : snappedPosition;

    if (button === 2) {
      // Prevent context menu from opening when right-clicking to close
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Check if we're in polygon mode and can close (use flag OR check directly)
      const currentShape = this.pointsArray[this.pointsArrayIndex];
      if (
        this.drawingMode === DrawingMode.Polygon &&
        currentShape &&
        currentShape.length >= 3
      ) {
        // Close the polygon
        this._pushHistorySnapshot();
        this._updateCursorPointFromEvent(event);
        this._closePolygons();
        this.pointsArrayIndex++;
        this.draw();
        this.onUpdate([...this.pointsArray]);
        return true;
      }
    } else if (button == 0) {
      // Prevent adding new points when editing a point/legend shape in edit mode
      if (
        this.initialPoints &&
        (this.drawingMode === DrawingMode.Point ||
          this.drawingMode === DrawingMode.Legend)
      ) {
        // Only allow moving existing points, not adding new ones
        return true;
      }

      this._pushHistorySnapshot();

      if (!this.hoveredLine && !this.hoveredPoint) {
        this._addPointToShape(cursorPosition);
      } else if (this.hoveredPoint) {
        this._addPointToShape(this.hoveredPoint.point);
      } else if (this.hoveredLine) {
        this._addPointOnLine(cursorPosition, this.hoveredLine);
      }
    }

    this._updateHoveredLineFromEvent(event);
    this._updateHoveredLineFromEvent(event);
    this.draw();
    this.onUpdate([...this.pointsArray]);
    return true;
  }

  handleDoubleClick(event: MouseEvent): boolean {
    if (this.hoveredPoint) {
      this.clear();
      this._pushHistorySnapshot();
      this._removePoint(this.hoveredPoint.point);
      this._updateHoveredPointFromEvent(event);
      this._updateHoveredLineFromEvent(event);
      this.draw();
      this.onUpdate([...this.pointsArray]);
      return true;
    }

    // Double-click closing removed - use right-click or click first vertex instead
    return false;
  }

  // eslint-disable-next-line complexity
  handleMouseMove(event: MouseEvent): boolean {
    // Handle vertex box selection drag
    if (this.vertexBoxActive && this.vertexBoxStart) {
      const x = event.clientX;
      const y = event.clientY - VIEWER_TOP_OFFSET;
      this._updateVertexBoxSelection(x, y);
      return true;
    }

    if (this.drawingMode === DrawingMode.MagicPolygon) {
      return this._handleMagicPolygonMouseMove(event);
    }

    // Allow viewer panning while previewing if right or middle button is held
    const isRightOrMiddleButtonDown =
      typeof event.buttons === 'number' &&
      ((event.buttons & 2) !== 0 || (event.buttons & 4) !== 0);

    // Handle dragging of points/edges
    if (this.activePoints.length) {
      this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );

      // Create or update drag feedback using actual geometry positions
      if (
        this.dragOriginPoint &&
        this.activePointerInitPosition &&
        this.activePointerCurrentPosition
      ) {
        // Compute the current geometry point from the actual mapped/constrained positions
        // This ensures we show the same movement that's visually applied to the shape
        const mappedPoints = this._mapPointsArrayAndActivePoints();
        let currentGeometryPoint: THREE.Vector3;

        if (
          this.activePointsMode === ActiveMode.Edge &&
          this.activePoints.length === 2
        ) {
          // For edges, find the current positions of the edge endpoints in the mapped array
          const [shapeIndex1, pointIndex1] = this.activePoints[0].locations[0];
          const [shapeIndex2, pointIndex2] = this.activePoints[1].locations[0];
          const currentStart = mappedPoints[shapeIndex1]?.[pointIndex1];
          const currentEnd = mappedPoints[shapeIndex2]?.[pointIndex2];

          if (currentStart && currentEnd) {
            currentGeometryPoint = new THREE.Vector3()
              .addVectors(currentStart, currentEnd)
              .multiplyScalar(0.5);
          } else {
            // Fallback: use simple translation
            const translation = new THREE.Vector3().subVectors(
              this.activePointerCurrentPosition,
              this.activePointerInitPosition
            );
            currentGeometryPoint = this.dragOriginPoint
              .clone()
              .add(translation);
          }
        } else {
          // For vertex, find the current position in the mapped array
          const [shapeIndex, pointIndex] = this.activePoints[0].locations[0];
          const currentPoint = mappedPoints[shapeIndex]?.[pointIndex];

          if (currentPoint) {
            currentGeometryPoint = currentPoint.clone();
          } else {
            // Fallback: use simple translation
            const translation = new THREE.Vector3().subVectors(
              this.activePointerCurrentPosition,
              this.activePointerInitPosition
            );
            currentGeometryPoint = this.activePoints[0].point
              .clone()
              .add(translation);
          }
        }

        // Store the direction for length submission
        const direction = new THREE.Vector3()
          .subVectors(currentGeometryPoint, this.dragOriginPoint)
          .normalize();
        this.dragDirection = direction;

        if (!this.dragFeedback) {
          // Create drag feedback on first movement
          this.dragFeedback = new ScreenSpaceDragFeedback(
            this.viewer,
            this.dragOriginPoint,
            currentGeometryPoint,
            (d) => this._viewerToDisplayUnits(d),
            true, // isDragging
            (newLength: number) => this._handleDragLengthSubmit(newLength),
            this._getDisplayUnitSuffix()
          );
        } else {
          // Update existing feedback during drag
          this.dragFeedback.setCurrentPoint(currentGeometryPoint);
          this.dragFeedback.setDragging(true);
        }
      }

      this._updateCursorPointFromEvent(event);
      this._updateHoveredPointFromEvent(null);
      this._updateHoveredLineFromEvent(null);
      this.draw();
      return true; // Consume event when actively dragging
    }

    // Update hover states
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Handle preview during active drawing
    // For Legend mode, always show preview even before first click
    const hasActiveDrawing =
      this.pointsArray[this.pointsArrayIndex]?.length > 0;
    const isLegendMode = this.drawingMode === DrawingMode.Legend;

    if (hasActiveDrawing || isLegendMode) {
      this.activePointerCurrentPosition = null;
      this._updateCursorPointFromEvent(event);
      this.draw();
      // During preview, don't consume when panning (right/middle held)
      return isRightOrMiddleButtonDown ? false : true;
    }

    // For completed shapes, only consume event if hovering over interactive elements
    if (this.hoveredPoint || this.hoveredLine) {
      this._updateCursorPointFromEvent(event);
      this.draw();
      // When panning with right/middle button, allow the viewer to handle it
      return isRightOrMiddleButtonDown ? false : true;
    }

    // Not interacting - allow panning
    return false;
  }

  private _setupDraggingForMagicPolygon(event: MouseEvent): void {
    if (
      !this.activePointsMode ||
      this.activePoints.length > 0 ||
      !this.activePointerInitPosition
    ) {
      return;
    }

    // Mouse has moved - set up dragging
    this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
      event,
      false
    );
    if (!this.activePointerCurrentPosition) {
      return;
    }

    // Only start dragging if mouse moved more than a small *pixel* threshold
    const pxDistance =
      distanceBetweenInPx(
        this.viewer,
        this.activePointerInitPosition,
        this.activePointerCurrentPosition
      ) ?? 0;
    if (pxDistance <= 3) {
      return;
    }

    this._setActivePointsForCurrentHover();
  }

  private _setActivePointsForCurrentHover(): void {
    if (this.activePointsMode === ActiveMode.Point && this.hoveredPoint) {
      this.activePoints = [this.hoveredPoint];
    } else if (this.activePointsMode === ActiveMode.Edge && this.hoveredLine) {
      this.activePoints = [
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.start,
          locations: findPointLocations(
            this.pointsArray,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.hoveredLine.start
          ),
        },
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.end,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          locations: findPointLocations(
            this.pointsArray,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.hoveredLine.end
          ),
        },
      ];
    }
  }

  private _handleMagicPolygonMouseMove(event: MouseEvent): boolean {
    const hasEditablePoints =
      this.pointsArray[this.pointsArrayIndex]?.length > 0;

    if (!hasEditablePoints) {
      // No editable points - don't consume events, allow panning
      return false;
    }

    // Check if we need to set up dragging (mouse started moving after button down)
    this._setupDraggingForMagicPolygon(event);

    // Only process mouse events if we're actively dragging points or hovering over interactive elements
    if (this.activePoints.length > 0) {
      // We're dragging points - handle the movement
      this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );
      this._updateCursorPointFromEvent(event);
      this._updateHoveredPointFromEvent(null);
      this._updateHoveredLineFromEvent(null);
      this.draw();
      return true; // Consume the event
    }

    // Check if we're hovering over interactive elements
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Only redraw if we're actually hovering over something interactive
    if (this.hoveredPoint || this.hoveredLine) {
      this._updateCursorPointFromEvent(event);
      this.draw();
      return true; // Consume the event
    }

    // Not hovering over anything interactive - don't consume the event
    return false;
  }

  private _updateHoveredPointFromEvent(event: MouseEvent | null) {
    this.hoveredPoint = null;

    if (!event) return;

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;
    const cursorPoint = this.viewer.impl.intersectGround(clientX, clientY);

    const pointsToCheck = this.pointsArray.flat();

    const hoveredPoint = pointsToCheck.find((point) => {
      const distance = distanceBetweenInPx(this.viewer, cursorPoint, point);
      return distance && distance <= 15;
    });

    // Find locations in the original points array
    const locations = hoveredPoint
      ? findPointLocations(this.pointsArray, hoveredPoint)
      : [];

    if (hoveredPoint) {
      this.hoveredPoint = {
        point: hoveredPoint,
        locations,
      };
    }
  }

  private _updateHoveredLineFromEvent(event: MouseEvent | null) {
    this.hoveredLineShape?.remove();
    this.hoveredLineShape = null;
    this.hoveredLine = null;

    if (!event) return;

    const cursorPoint = this._getWorldPositionFromMouseEvent(event);
    if (!cursorPoint) return;

    const lines = this.pointsArray
      .flatMap((points) => {
        const isClosed =
          points.length > 2 && points[0].equals(points[points.length - 1]);
        return points
          .map((point, index) => {
            const nextPoint =
              points[index + 1] || (isClosed ? points[0] : undefined);
            if (!nextPoint || point.equals(nextPoint)) return null;

            // https://threejs.org/docs/#api/en/math/Line3
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const line = new THREE.Line3(point, nextPoint);
            // https://threejs.org/docs/#api/en/math/Line3
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const closestPoint = line.closestPointToPoint(cursorPoint, true);
            const distance = distanceBetweenInPx(
              this.viewer,
              cursorPoint,
              closestPoint
            );

            return distance !== null && distance < 5
              ? { line, distance }
              : null;
          })
          .filter(Boolean) as { line: THREE.Line3; distance: number }[];
      })
      .sort((a, b) => a.distance - b.distance);

    this.hoveredLine = lines.length > 0 ? lines[0].line : null;
  }

  private _getWorldPositionFromMouseEvent(
    event: MouseEvent,
    useSnapping = true
  ) {
    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    const cursorPositionRaw = this.viewer.impl.intersectGround(
      clientX,
      clientY
    );

    // Disable snapping for MagicPolygon mode
    const shouldUseSnapping =
      useSnapping && this.drawingMode !== DrawingMode.MagicPolygon;

    if (shouldUseSnapping) {
      // Get the snap point indicator and update it
      const snapPoint = getSnapPointIndicator(this.snapper, this.viewer);
      this.snappingIndicator?.setSnapPoint(snapPoint);
    } else {
      // Hide snapping indicator when snapping is disabled
      this.snappingIndicator?.setSnapPoint(null);
    }

    // Always return the raw cursor position - snapping is handled separately on click
    return cursorPositionRaw;
  }

  /**
   * Get the current snap point if snapping is active, otherwise return the provided point.
   * Used when placing points to snap to the indicator position.
   */
  private _getSnappedPoint(rawPoint: THREE.Vector3): THREE.Vector3 {
    const snapPoint = this.snappingIndicator?.getSnapPoint();
    return snapPoint ? snapPoint : rawPoint;
  }

  private _closePolygons() {
    this.pointsArray
      .filter((points) => points.length > 2)
      .forEach((points) => {
        const startPoint = points[0];
        const endPoint = points[points.length - 1];

        if (startPoint && endPoint && !startPoint.equals(endPoint)) {
          points.push(startPoint);
        }
      });
  }

  private _calculateEndPoint(
    previousPoint: THREE.Vector3,
    currentPoint: THREE.Vector3,
    shiftKey: boolean
  ) {
    const angle = angleBetweenTwoPoints([previousPoint, currentPoint]);
    const snappedAngle = snapAngle(angle, MEASUREMENT_TOOL_SNAPPING_ANGLE_STEP);

    if (shiftKey) {
      return endPointByAngle(
        previousPoint,
        snappedAngle,
        previousPoint.distanceTo(currentPoint)
      );
    } else {
      return currentPoint;
    }
  }

  private _mapPointsArrayAndActivePoints() {
    if (
      !this.activePointsMode ||
      !this.activePointerCurrentPosition ||
      !this.activePointerInitPosition
    ) {
      return this.pointsArray;
    }

    const movementVector = new THREE.Vector3(
      this.activePointerCurrentPosition.x - this.activePointerInitPosition.x,
      this.activePointerCurrentPosition.y - this.activePointerInitPosition.y
    );

    return this.pointsArray.map((points, shapeIndex) => {
      const totalPointsLength = points.length;
      return points
        .map((point, pointIndex) => {
          // Skip if the original point is null
          if (!point) return null;

          return this._mapPointWithActivePoints(
            point,
            pointIndex,
            shapeIndex,
            points,
            totalPointsLength,
            movementVector
          );
        })
        .filter((point): point is THREE.Vector3 => point !== null); // Filter out null points
    });
  }

  private _mapPointWithActivePoints(
    point: THREE.Vector3,
    pointIndex: number,
    shapeIndex: number,
    points: THREE.Vector3[],
    totalPointsLength: number,
    movementVector: THREE.Vector3
  ): THREE.Vector3 {
    const isMovingWithoutPreservingAngle =
      this.activePointsMode !== ActiveMode.Edge || totalPointsLength <= 2;

    const isActive = (
      shapeIndex: number,
      pointIndex: number,
      points: THREE.Vector3[]
    ) => {
      const getCanonicalIndex = (points: THREE.Vector3[], index: number) =>
        !(
          index === points.length - 1 &&
          points[0].x === points[index].x &&
          points[0].y === points[index].y
        )
          ? index
          : 0;

      const canonicalIndex = getCanonicalIndex(points, pointIndex);
      return this.activePoints.some(({ locations }) =>
        locations.some(([si, pi]) => si === shapeIndex && pi === canonicalIndex)
      );
    };

    if (!isActive(shapeIndex, pointIndex, points)) {
      return point;
    }

    if (isMovingWithoutPreservingAngle) {
      return new THREE.Vector3(
        point.x + movementVector.x,
        point.y + movementVector.y
      );
    }

    const firstIndex = 0;
    const lastIndex = points.length - 1;
    const prevIndex = (pointIndex - 1 + points.length) % points.length;
    const nextIndex = (pointIndex + 1) % points.length;
    const prev = points[prevIndex];
    const next = points[nextIndex];

    let neighbor: THREE.Vector3 | undefined = undefined;
    if (prev && !isActive(shapeIndex, prevIndex, points)) {
      neighbor = prev;
    } else if (next && !isActive(shapeIndex, nextIndex, points)) {
      neighbor = next;
    } else {
      return point; // brak sąsiada spoza aktywnych
    }

    const originalEdge = new THREE.Vector3(
      point.x - neighbor.x,
      point.y - neighbor.y
    );
    const edgeDirection = originalEdge.clone().normalize();
    const distance = movementVector.dot(edgeDirection);
    const movedPoint = new THREE.Vector3(
      point.x + edgeDirection.x * distance,
      point.y + edgeDirection.y * distance
    );

    if (pointIndex === firstIndex) {
      points[lastIndex] = movedPoint.clone();
    } else if (pointIndex === lastIndex) {
      points[firstIndex] = movedPoint.clone();
    }

    return movedPoint;
  }

  // eslint-disable-next-line complexity
  private _isShapeValid(points: THREE.Vector3[], mode: DrawingMode): boolean {
    switch (mode) {
      case DrawingMode.Point:
      case DrawingMode.Legend:
        return points.length === 1;
      case DrawingMode.Polyline:
        return points.length >= 2;
      case DrawingMode.Polygon:
        // For polygon, need at least 3 points and the first and last should be the same (closed)
        return (
          points.length >= 4 && points[0].equals(points[points.length - 1])
        );
      case DrawingMode.MagicPolygon:
        // Magic polygon is valid if it has at least 1 point (for single click)
        return points.length >= 1;
      case DrawingMode.Rectangle:
        // For rectangle, need exactly 5 points (4 corners + closing point)
        return (
          points.length === 5 && points[0].equals(points[points.length - 1])
        );
      case DrawingMode.DrainLine:
        // For drain lines, need at least 2 points (start and end)
        return points.length >= 2;
      case DrawingMode.RoofOutline:
        // Roof outline uses the same validation as polygon (it's just a UI mode)
        return (
          points.length >= 4 && points[0].equals(points[points.length - 1])
        );
      default:
        return false;
    }
  }

  private _createRectanglePreview(
    firstPoint: THREE.Vector3,
    cursorPoint: THREE.Vector3,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    const x1 = firstPoint.x;
    const y1 = firstPoint.y;
    const x2 = cursorPoint.x;
    const y2 = cursorPoint.y;
    const rectanglePoints = [
      firstPoint,
      new THREE.Vector3(x2, y1, 0),
      new THREE.Vector3(x2, y2, 0),
      new THREE.Vector3(x1, y2, 0),
      firstPoint,
    ];

    // Create filled rectangle preview using polygon shape
    const previewShapes = mapPointsToPolygonShape(
      [rectanglePoints],
      ...commonArgs,
      {
        default: InvalidShapeStyle,
        hovered: InvalidShapeStyle,
        selected: InvalidShapeStyle,
      }
    );

    return previewShapes;
  }

  private _createPolylineShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For editing mode (when we have initial points), check validity of all existing shapes
    // For drawing mode, check validity of the current shape being drawn
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const lineStyle = isValid ? ValidLineStyle : InvalidLineStyle;
    return mapPointsToPolylineShape(pointsWithCursor, ...commonArgs, {
      default: lineStyle,
      hovered: lineStyle,
      selected: lineStyle,
    });
  }

  private _createPolygonShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For editing mode (when we have initial points), check validity of all existing shapes
    // For drawing mode, check validity of the current shape being drawn
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const shapeStyle = isValid ? ValidShapeStyle : InvalidShapeStyle;

    const shapes = mapPointsToPolygonShape(pointsWithCursor, ...commonArgs, {
      default: shapeStyle,
      hovered: shapeStyle,
      selected: shapeStyle,
    });

    return shapes;
  }

  private _createSplitLineShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For drain lines, create split line shapes with dashed style
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const lineStyle = isValid ? ValidLineStyle : InvalidLineStyle;

    // Create drain line-specific style with dashed lines
    const drainLineStyle = {
      ...lineStyle,
      lineColor: 'blue',
      lineWidth: 2,
      lineStyle: 3, // DashedLarge
    };

    return mapPointsToSplitLineShape(pointsWithCursor, ...commonArgs, {
      default: drainLineStyle,
      hovered: drainLineStyle,
      selected: drainLineStyle,
    });
  }

  // Legend preview dimensions (in viewer coordinates, matching LegendShape constants)
  private static readonly LEGEND_PREVIEW_WIDTH = 2.0; // MIN_WIDTH from LegendShape
  private static readonly LEGEND_PREVIEW_HEIGHT = 1.0; // Approximate height for preview

  private _createLegendPreviewShape(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For legend, we store the point in pointsArray[0][0] after clicking
    // Check if there's a placed point (any point in the pointsArray)
    const placedPoint = this.pointsArray[0]?.[0];
    const hasPlacedPoint = !!placedPoint;

    let position: THREE.Vector3 | null = null;

    if (hasPlacedPoint) {
      // Use the placed point (show green preview at confirmed location)
      position = placedPoint;
    } else if (this.cursorPoint) {
      // Use cursor position for hover preview (show orange)
      position = this.cursorPoint;
    }

    if (!position) {
      return [];
    }

    // Create rectangle corners for the legend preview
    // Legend position is top-left corner, extends right and down
    const width = ShapesDrawingTool.LEGEND_PREVIEW_WIDTH;
    const height = ShapesDrawingTool.LEGEND_PREVIEW_HEIGHT;

    const rectanglePoints = [
      new THREE.Vector3(position.x, position.y, 0), // Top-left
      new THREE.Vector3(position.x + width, position.y, 0), // Top-right
      new THREE.Vector3(position.x + width, position.y - height, 0), // Bottom-right
      new THREE.Vector3(position.x, position.y - height, 0), // Bottom-left
      new THREE.Vector3(position.x, position.y, 0), // Close the polygon
    ];

    // Use green if point is placed, orange if hovering
    // Reuse existing style definitions for consistency
    const previewStyle = hasPlacedPoint ? ValidShapeStyle : InvalidShapeStyle;

    return mapPointsToPolygonShape([rectanglePoints], ...commonArgs, {
      default: previewStyle,
      hovered: previewStyle,
      selected: previewStyle,
    });
  }

  private _renderVertices(
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData],
    mappedPointsArray: THREE.Vector3[][]
  ): void {
    // Clear existing screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.remove());
    this.screenSpaceVertices = [];

    // Render all vertices for all shapes (current and completed)
    mappedPointsArray.forEach((points, shapeIndex) => {
      // Skip if points array is null, undefined, or empty
      if (!points || points.length === 0) {
        return;
      }

      const isPreviewShape =
        this.drawingMode === DrawingMode.Rectangle &&
        this.pointsArray[this.pointsArrayIndex]?.length === 1 &&
        this.cursorPoint &&
        shapeIndex === mappedPointsArray.length - 1;

      if (this.drawingMode === DrawingMode.Rectangle && points.length === 5) {
        // For rectangles, only show the 4 corner vertices (skip the closing point)
        const cornerPoints = points.slice(0, 4);
        cornerPoints.forEach((point, pointIndex) => {
          // Skip if point is null or undefined
          if (!point) {
            return;
          }

          // Create screen-space vertex with a getter that references the mapped points array
          const isFirstVertex = pointIndex === 0;
          const baseStyle = isPreviewShape
            ? VertexStyle
            : isFirstVertex
            ? FirstVertexStyle
            : VertexStyle;
          const isSelected = this._isPointSelected(point);
          const style = isSelected ? FirstVertexStyle : baseStyle;

          try {
            const vertex = new ScreenSpaceVertex(
              this.viewer,
              () => mappedPointsArray[shapeIndex][pointIndex],
              style,
              isFirstVertex
            );
            this.screenSpaceVertices.push(vertex);
          } catch (error) {
            console.error(
              `_renderVertices: Failed to create ScreenSpaceVertex for rectangle point ${pointIndex}`,
              { point, error }
            );
          }
        });
      } else {
        // For other shapes, show all vertices
        points.forEach((point, pointIndex) => {
          // Skip if point is null or undefined
          if (!point) {
            return;
          }

          // Create screen-space vertex with a getter that references the mapped points array
          const isFirstVertex = pointIndex === 0;
          const baseStyle = isFirstVertex ? FirstVertexStyle : VertexStyle;
          const isSelected = this._isPointSelected(point);
          const style = isSelected ? FirstVertexStyle : baseStyle;

          try {
            const vertex = new ScreenSpaceVertex(
              this.viewer,
              () => mappedPointsArray[shapeIndex][pointIndex],
              style,
              isFirstVertex
            );
            this.screenSpaceVertices.push(vertex);
          } catch (error) {
            console.error(
              `_renderVertices: Failed to create ScreenSpaceVertex for regular point ${pointIndex}`,
              { point, error }
            );
          }
        });
      }
    });
  }

  // Add method to force vertex updates when points change
  private _updateVertices(): void {
    // Force update all screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.update());
  }

  // Determine label color based on shape validity and whether it's being drawn
  private _getLabelColorForShape(
    points: THREE.Vector3[],
    shapeIndex: number
  ): LengthLabelColor {
    const isCurrentShape = shapeIndex === this.pointsArrayIndex;

    // For polylines, they're always valid once they have 2+ points (no need to close)
    // So use green for drawing mode as well
    if (
      this.drawingMode === DrawingMode.Polyline ||
      this.drawingMode === DrawingMode.DrainLine
    ) {
      return points.length >= 2 ? 'green' : 'orange';
    }

    if (isCurrentShape) {
      // Current shape being drawn - use orange until complete
      return 'orange';
    }
    // Completed shapes - check validity
    const isValid = this._isShapeValid(points, this.drawingMode);
    return isValid ? 'green' : 'orange';
  }

  // Check if an edge should be styled as "intermediate" (the edge being actively drawn)
  private _isIntermediateEdge(
    shapeIndex: number,
    edgeIndex: number,
    totalEdges: number
  ): boolean {
    const isCurrentShape = shapeIndex === this.pointsArrayIndex;
    const isLastEdge = edgeIndex === totalEdges - 1;
    const isRectangleMode = this.drawingMode === DrawingMode.Rectangle;
    return (
      isCurrentShape &&
      isLastEdge &&
      this.cursorPoint !== null &&
      !isRectangleMode
    );
  }

  // Render length labels for all edges in the shapes
  private _renderLengthLabels(mappedPointsArray: THREE.Vector3[][]): void {
    // Clear existing length labels
    this.screenSpaceLengthLabels.forEach((label) => label.remove());
    this.screenSpaceLengthLabels = [];

    // Render length labels for all edges
    mappedPointsArray.forEach((points, shapeIndex) => {
      if (!points || points.length < 2) {
        return;
      }

      const labelColor = this._getLabelColorForShape(points, shapeIndex);
      const totalEdges = points.length - 1;

      // Create labels for each edge (segment between consecutive points)
      for (let i = 0; i < totalEdges; i++) {
        const startPoint = points[i];
        const endPoint = points[i + 1];

        if (!startPoint || !endPoint) {
          continue;
        }

        const isIntermediate = this._isIntermediateEdge(
          shapeIndex,
          i,
          totalEdges
        );
        const variant: LengthLabelVariant = isIntermediate
          ? 'intermediate'
          : 'fixed';

        try {
          const label = new ScreenSpaceLengthLabel(
            this.viewer,
            () => mappedPointsArray[shapeIndex][i],
            () => mappedPointsArray[shapeIndex][i + 1],
            (d) => this._viewerToDisplayUnits(d),
            variant,
            labelColor,
            this._getDisplayUnitSuffix()
          );
          this.screenSpaceLengthLabels.push(label);
        } catch (error) {
          console.error(
            `_renderLengthLabels: Failed to create label for edge ${i}`,
            { error }
          );
        }
      }
    });
  }

  // Update length labels positions
  private _updateLengthLabels(): void {
    this.screenSpaceLengthLabels.forEach((label) => label.update());
  }

  // Handle length submission from drag feedback
  private _handleDragLengthSubmit(newLength: number): void {
    if (
      !this.dragFeedback ||
      !this.dragOriginPoint ||
      !this.dragDirection ||
      !this.dragCurrentPoint
    ) {
      return;
    }

    // Convert input length from display units to viewer units
    const viewerDistance = this._displayUnitsToViewer(newLength);

    // Calculate the new geometry position (from origin, in the drag direction)
    const newPosition = new THREE.Vector3()
      .copy(this.dragOriginPoint)
      .add(this.dragDirection.clone().multiplyScalar(viewerDistance));

    // Calculate the translation from CURRENT position to new position
    // This is the incremental move needed from where the geometry currently is
    const translation = new THREE.Vector3().subVectors(
      newPosition,
      this.dragCurrentPoint
    );

    // Apply translation to the dragged points
    if (this.draggedPoints.length > 0) {
      this._pushHistorySnapshot();

      // Apply the translation to each dragged point in the pointsArray
      // PointLocation is a tuple [shapeIndex, pointIndex]
      this.draggedPoints.forEach((draggedPoint) => {
        draggedPoint.locations.forEach((location) => {
          const [shapeIndex, pointIndex] = location;
          const point = this.pointsArray[shapeIndex]?.[pointIndex];
          if (point) {
            point.add(translation);
          }
        });
      });

      // Redraw and update
      this.draw();
      this.onUpdate([...this.pointsArray]);
    }

    // Update the current position for subsequent submissions
    this.dragCurrentPoint = newPosition.clone();

    // Update the drag feedback to show the new position (for subsequent edits)
    this.dragFeedback.setCurrentPoint(newPosition);

    // Keep draggedPoints for subsequent submissions - don't clear them
  }

  getResults(): {
    mode: DrawingMode;
    points: THREE.Vector3[][];
  } {
    return {
      mode: this.drawingMode,
      points: this.pointsArray,
    };
  }

  // Helper to get sample points
  getSamplePoints(): THREE.Vector3[] {
    return this.magicPolygonHandler.getSamplePoints();
  }

  // Set loading state for magic polygon
  setMagicPolygonLoading(loading: boolean): void {
    this.isMagicPolygonLoading = loading;
  }

  updateMagicPolygonResults(results: any) {
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // Snapshot before applying new MagicPolygon results so undo restores pre-result state
      this._pushHistorySnapshot();

      this.magicPolygonHandler.updateResults(results);

      // Convert the API result into editable points
      if (results.valid && results.multipolygon) {
        const editablePoints = this.magicPolygonHandler.convertToEditablePoints(
          results.multipolygon,
          this.viewer,
          this.activeSheetData
        );

        if (editablePoints.length > 0) {
          this.pointsArray = [editablePoints];
          this.pointsArrayIndex = 0;
        }
      }

      this.draw(); // Redraw to show the new results
    }
  }

  /**
   * Get the current edited multipolygon for MagicPolygon mode
   * This allows the hook to access the edited polygon data for submission
   */
  getCurrentEditedMagicPolygon(): any {
    if (this.drawingMode !== DrawingMode.MagicPolygon) {
      return null;
    }

    // If we have edited points, convert them to multipolygon format
    if (this.magicPolygonHandler.hasEditedPoints(this.pointsArray)) {
      return this.magicPolygonHandler.convertEditedPointsToMultipolygon(
        this.pointsArray,
        this.viewer,
        this.activeSheetData
      );
    }

    return null;
  }

  private _renderPolygon() {
    // Clear existing shapes
    this.clear();

    const commonArgs: [Viewer3D, Edit2D, ActiveSheetData] = [
      this.viewer,
      this.edit2d,
      this.activeSheetData,
    ];

    // Map points with active points for real-time dragging
    const mappedPointsArray = this._mapPointsArrayAndActivePoints();

    // Create polygon shapes
    this.shapes = this._createPolygonShapes(
      mappedPointsArray,
      this.pointsArrayIndex,
      commonArgs
    );

    // Draw the shapes to make them visible
    this.shapes.forEach((shape) => {
      shape.setSelected(true);
      shape.draw();
    });

    // Render vertices for interactivity
    this._renderVertices(commonArgs, mappedPointsArray);

    // Render length labels on edges
    this._renderLengthLabels(mappedPointsArray);
  }

  // eslint-disable-next-line complexity
  draw() {
    // Clear existing shapes
    this.clear();

    const commonArgs: [Viewer3D, Edit2D, ActiveSheetData] = [
      this.viewer,
      this.edit2d,
      this.activeSheetData,
    ];

    // Map points with active points for real-time dragging
    const mappedPointsArray = this._mapPointsArrayAndActivePoints();

    // Create pointsWithCursor array that includes cursor point for live previews
    const pointsWithCursor = mappedPointsArray.map((points, index) => {
      if (index === this.pointsArrayIndex && this.cursorPoint) {
        return [...points, this.cursorPoint];
      }
      return points;
    });

    // Create shapes based on drawing mode
    switch (this.drawingMode) {
      case DrawingMode.Point:
        this.shapes = this._createPolylineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.Legend:
        this.shapes = this._createLegendPreviewShape(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.Polyline:
        this.shapes = this._createPolylineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.Polygon:
      case DrawingMode.RoofOutline:
        this.shapes = this._createPolygonShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.MagicPolygon:
        // For MagicPolygon, render the API results as Edit2D shapes
        if (this.magicPolygonHandler.hasValidResults()) {
          // If we have editable points, render them as a regular polygon for interactivity
          if (this.pointsArray[this.pointsArrayIndex]?.length > 0) {
            this._renderPolygon();
            // Return early to prevent the main draw() method from continuing
            // since _renderPolygon() already handles shape creation and drawing
            return;
          } else {
            // Otherwise, render the API results as static shapes
            const results = this.magicPolygonHandler.getResults();
            if (results?.multipolygon) {
              this.shapes = this.magicPolygonHandler.createShapes(
                results.multipolygon,
                commonArgs
              );
              // The MagicPolygonHandler.createShapes() method already calls draw() on the shapes
              // so we don't need to call draw() again in the main draw() method
              // Just return early to prevent the main draw() method from continuing
              return;
            }
          }
        }
        // If we have points (carried over or clicked) but no API results, still render as a polygon
        if (this.pointsArray[this.pointsArrayIndex]?.length > 0) {
          this._renderPolygon();
          return;
        }
        // If we're waiting for API results, don't render anything (just show loading cursor)
        break;
      case DrawingMode.Rectangle:
        if (
          this.pointsArray[this.pointsArrayIndex]?.length === 1 &&
          this.cursorPoint
        ) {
          // Create a combined array with completed rectangles and preview
          const completedRectangles = this.pointsArray
            .slice(0, this.pointsArrayIndex)
            .filter((points) => points.length > 0);

          // Create preview rectangle points
          const firstPoint = this.pointsArray[this.pointsArrayIndex][0];
          const x1 = firstPoint.x;
          const y1 = firstPoint.y;
          const x2 = this.cursorPoint.x;
          const y2 = this.cursorPoint.y;
          const previewRectanglePoints = [
            firstPoint,
            new THREE.Vector3(x2, y1, 0),
            new THREE.Vector3(x2, y2, 0),
            new THREE.Vector3(x1, y2, 0),
            firstPoint,
          ];

          // Combine completed rectangles with preview for vertex rendering
          const combinedPointsArray = [
            ...completedRectangles,
            previewRectanglePoints,
          ];

          // Show completed rectangles
          if (completedRectangles.length > 0) {
            const completedShapes = this._createPolygonShapes(
              completedRectangles,
              this.pointsArrayIndex - 1,
              commonArgs
            );
            this.shapes.push(...completedShapes);
          }

          // Show rectangle preview after first click
          const previewShapes = this._createRectanglePreview(
            firstPoint,
            this.cursorPoint,
            commonArgs
          );
          this.shapes.push(...previewShapes);

          // Render vertices for all shapes (completed + preview)
          this._renderVertices(commonArgs, combinedPointsArray);

          // Render length labels on edges
          this._renderLengthLabels(combinedPointsArray);

          // Early return to avoid duplicate rendering at the end of draw()
          this.shapes.forEach((shape) => {
            shape.setSelected(true);
            shape.draw();
          });
          this._updateVertices();
          return;
        } else {
          // Show completed rectangles
          this.shapes = this._createPolygonShapes(
            mappedPointsArray,
            this.pointsArrayIndex,
            commonArgs
          );
        }
        break;
      case DrawingMode.DrainLine:
        // For drain lines, create split line shapes
        this.shapes = this._createSplitLineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
    }

    this.shapes.forEach((shape) => {
      shape.setSelected(true);
      shape.draw();
    });

    // Render all vertices (clicked points) for the current shape using mapped points
    // Note: For MagicPolygon, vertices are already rendered above
    if (this.drawingMode !== DrawingMode.MagicPolygon) {
      this._renderVertices(commonArgs, mappedPointsArray);
    }

    // Render length labels on edges (using pointsWithCursor to include intermediate edge)
    this._renderLengthLabels(pointsWithCursor);

    // Force update vertices to ensure they reflect the current point positions
    this._updateVertices();

    // Render hovered line if any
    if (this.hoveredLine) {
      // https://threejs.org/docs/#api/en/math/Line3
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const points = [this.hoveredLine.start, this.hoveredLine.end];
      this.hoveredLineShape = new PolylineShape(
        ...commonArgs,
        undefined,
        points,
        {
          default: HoveredValidLineStyle,
          hovered: HoveredValidLineStyle,
          selected: HoveredValidLineStyle,
        }
      );
      this.hoveredLineShape.setHovered(true);
      this.hoveredLineShape.draw();
    }

    // Force layer update to ensure shapes are visible
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      const layer = this.edit2d.defaultContext.layer;
      if (typeof (layer as any).update === 'function') {
        (layer as any).update();
      }
    }
  }

  clear() {
    this.shapes.forEach((shape) => shape.remove());
    this.shapes = [];
    this.hoveredLineShape?.remove();
    this.hoveredLineShape = null;

    // Clear screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.remove());
    this.screenSpaceVertices = [];

    // Clear screen-space length labels
    this.screenSpaceLengthLabels.forEach((label) => label.remove());
    this.screenSpaceLengthLabels = [];

    // NOTE: Do NOT clear drag feedback here - it persists across draw() calls
    // It's cleared explicitly in _handleRegularButtonDown, _handleMagicPolygonButtonDown,
    // _handleRegularButtonUp (if no movement), and on undo/redo
  }

  private _removePoint(toRemove: THREE.Vector3) {
    this.pointsArray = this.pointsArray
      .map((points) => {
        // Check if the polygon was closed before removal
        const wasClosed =
          points.length > 2 && points[0].equals(points[points.length - 1]);

        // Check if we're removing the first/last point (which are the same in a closed polygon)
        const isRemovingClosingPoint = wasClosed && points[0].equals(toRemove);

        // Filter out the point(s) to remove
        const filteredPoints = points.filter(
          (point) => !point.equals(toRemove)
        );

        // If we removed the closing point from a closed polygon, re-close it
        // by adding the new first point to the end
        if (
          isRemovingClosingPoint &&
          filteredPoints.length >= 3 &&
          !filteredPoints[0].equals(filteredPoints[filteredPoints.length - 1])
        ) {
          filteredPoints.push(filteredPoints[0].clone());
        }

        return filteredPoints;
      })
      .filter((points) => points.length);
    this.pointsArrayIndex = this.pointsArray.length;
  }

  // eslint-disable-next-line complexity
  private _addPointToShape(point: THREE.Vector3) {
    if (!this.pointsArray[this.pointsArrayIndex])
      this.pointsArray[this.pointsArrayIndex] = [];

    const firstPoint = this.pointsArray[this.pointsArrayIndex][0];
    const distance = distanceBetweenInPx(this.viewer, point, firstPoint);
    const snapped = getSnapPointByDistance(point, firstPoint, distance);

    switch (this.drawingMode) {
      case DrawingMode.Point:
        this.pointsArray[this.pointsArrayIndex].push(point);
        this.pointsArrayIndex++;
        break;
      case DrawingMode.Legend:
        // For legend, replace any existing point (only one point allowed)
        // Reset to first index and replace the point
        this.pointsArray = [[point]];
        this.pointsArrayIndex = 1;
        break;
      case DrawingMode.Polyline:
      case DrawingMode.Polygon:
      case DrawingMode.MagicPolygon:
        this.pointsArray[this.pointsArrayIndex].push(snapped);
        if (firstPoint?.equals(snapped)) this.pointsArrayIndex++;
        break;
      case DrawingMode.Rectangle:
        if (this.pointsArray[this.pointsArrayIndex].length === 0) {
          // First point of rectangle
          this.pointsArray[this.pointsArrayIndex].push(snapped);
        } else if (this.pointsArray[this.pointsArrayIndex].length === 1) {
          // Second point of rectangle - create the full rectangle
          const firstCorner = this.pointsArray[this.pointsArrayIndex][0];
          const secondCorner = snapped;

          // Create rectangle points in clockwise order
          const x1 = firstCorner.x;
          const y1 = firstCorner.y;
          const x2 = secondCorner.x;
          const y2 = secondCorner.y;

          // Add the remaining corners to complete the rectangle
          this.pointsArray[this.pointsArrayIndex].push(
            new THREE.Vector3(x2, y1, 0)
          ); // Top right
          this.pointsArray[this.pointsArrayIndex].push(secondCorner); // Bottom right
          this.pointsArray[this.pointsArrayIndex].push(
            new THREE.Vector3(x1, y2, 0)
          ); // Bottom left
          this.pointsArray[this.pointsArrayIndex].push(firstCorner); // Close the rectangle

          this.pointsArrayIndex++;
        }
        break;
      case DrawingMode.DrainLine:
        if (this.pointsArray[this.pointsArrayIndex].length === 0) {
          // First point of drain line
          this.pointsArray[this.pointsArrayIndex].push(snapped);
        } else if (this.pointsArray[this.pointsArrayIndex].length === 1) {
          // Second point of drain line - create the full line
          const endPoint = snapped;

          // Add the end point to complete the line
          this.pointsArray[this.pointsArrayIndex].push(endPoint);

          this.pointsArrayIndex++;
        }
        break;
    }
  }

  private _addPointOnLine(point: THREE.Vector3, line: THREE.Line3) {
    // https://threejs.org/docs/#api/en/math/Line3
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const pointOnLine = line.closestPointToPoint(point, true);
    // https://threejs.org/docs/#api/en/math/Line3
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { start, end } = line;

    this.pointsArray = this.pointsArray.map((points) => {
      const startIndex = points.findIndex((p) => p.equals(start));
      const endIndex = points.findLastIndex((p) => p.equals(end));

      if (startIndex === -1 || endIndex === -1) return points;

      const insertIndex = Math.min(startIndex, endIndex) + 1;
      const updated = [...points];

      updated.splice(insertIndex, 0, pointOnLine.clone());

      return updated;
    });
  }

  private _shouldShowCursorPoint(): boolean {
    // In edit mode, only show cursor point for polylines or when creating new shapes
    if (this.initialPoints && this.drawingMode !== DrawingMode.Polyline) {
      // For point/legend drawing mode in edit mode, never show cursor point
      if (
        this.drawingMode === DrawingMode.Point ||
        this.drawingMode === DrawingMode.Legend
      ) {
        return false;
      }

      // Check if we're currently creating a new shape (pointsArrayIndex points to a new shape)
      const currentShape = this.pointsArray[this.pointsArrayIndex];
      if (!currentShape || currentShape.length === 0) {
        // Not creating a new shape, so don't show cursor point
        return false;
      }
    }
    return true;
  }

  private _updateCursorPointFromEvent(event: MouseEvent) {
    // For MagicPolygon, don't show cursor point at all since it's not needed
    if (
      this.drawingMode === DrawingMode.MagicPolygon ||
      !this._shouldShowCursorPoint()
    ) {
      this.cursorPoint = null;
      return;
    }

    // Only show cursor point if we're actively drawing a new shape
    // (i.e., the current shape index has points and we're not just editing existing shapes)
    // Exception: Legend mode shows preview before any points are placed
    const currentShape = this.pointsArray[this.pointsArrayIndex];
    const isLegendMode = this.drawingMode === DrawingMode.Legend;
    const hasActiveShape = currentShape && currentShape.length > 0;

    if (!isLegendMode && !hasActiveShape) {
      this.cursorPoint = null;
      return;
    }

    // Use the snapped position for cursor preview
    const point = this._getWorldPositionFromMouseEvent(event);
    if (!point) {
      this.cursorPoint = null;
      return;
    }

    this.cursorPoint = this._calculateCursorPoint(event, point, currentShape);
  }

  private _calculateCursorPoint(
    event: MouseEvent,
    point: THREE.Vector3,
    currentShape: THREE.Vector3[] | undefined
  ): THREE.Vector3 {
    const firstPoint = currentShape?.[0];
    const lastPoint = currentShape?.[currentShape.length - 1];

    if (event.shiftKey && lastPoint) {
      return this._calculateEndPoint(lastPoint, point, true);
    } else if (this.hoveredPoint) {
      return this.hoveredPoint.point;
    } else if (firstPoint) {
      const distance = distanceBetweenInPx(this.viewer, point, firstPoint);
      return getSnapPointByDistance(point, firstPoint, distance);
    }
    return point;
  }
}
